/**
 * Which of a tenant's validations the staff screens may issue — the "Issue
 * validation" modal and the tablet kiosk.
 *
 * ⚠️ `deliveryMethod` is NOT an exclusive type, and must never be used to gate
 * this list. It records where a code is ALSO published (a coupon slip, a
 * printed QR); it says nothing about who may hand the validation out. Every
 * validation that names a zone already carries a QR (`arrivalUrl`, built in the
 * API's list route independently of this axis) and can be issued by plate or by
 * texting a link. Gating on `deliveryMethod === 'evalidation'` made a
 * QR-configured program invisible here, so tenants had to create the same
 * program twice and read its numbers in two halves.
 *
 * The axis still means something in exactly one place: `GET /v1/validations`,
 * the POS-integrator discovery list. That answers "which code should an
 * integrator redeem against", not "who may issue this".
 */
export function issuableOffers<T extends { active?: boolean; type?: string; value?: number }>(all: T[]): T[] {
  return all
    .filter(v => v.active !== false)
    .map((offer, index) => ({ offer, index }))
    .sort((a, b) => {
      // Staff scan time grants as a ladder. The API's storage order is not a
      // product order, so 3 hours could otherwise appear before 90 minutes.
      // Money/full/until offers have no comparable duration; leave those in
      // their original relative order after the duration ladder.
      const aMinutes = (a.offer.type === 'minutes' || a.offer.type === 'freemax') && typeof a.offer.value === 'number'
        ? a.offer.value : null
      const bMinutes = (b.offer.type === 'minutes' || b.offer.type === 'freemax') && typeof b.offer.value === 'number'
        ? b.offer.value : null
      if (aMinutes !== null && bMinutes !== null) return aMinutes - bMinutes || a.index - b.index
      if (aMinutes !== null) return -1
      if (bMinutes !== null) return 1
      return a.index - b.index
    })
    .map(({ offer }) => offer)
}
